import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Users, 
  TrendingUp, 
  DollarSign, 
  Eye, 
  Globe, 
  BarChart3,
  Calendar,
  Flame,
  BookOpen,
  Bookmark,
  Volume2,
  Share2,
  RefreshCw
} from 'lucide-react';

const StatCard = ({ title, value, subtitle, icon: Icon, trend }) => (
  <Card className="p-5 border border-primary/10">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-muted-foreground">{title}</p>
        <p className="text-2xl font-semibold mt-1">{value}</p>
        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      <div className="p-2 rounded-full bg-primary/10">
        <Icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
      </div>
    </div>
    {trend !== undefined && (
      <div className={`text-xs mt-2 ${trend >= 0 ? 'text-green-600' : 'text-red-500'}`}>
        {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}% from last period
      </div>
    )}
  </Card>
);

const ChartBar = ({ label, value, maxValue, color = 'bg-primary' }) => {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
  return (
    <div className="flex items-center gap-3 py-1">
      <span className="text-xs text-muted-foreground w-24 truncate" title={label}>{label}</span>
      <div className="flex-1 h-4 bg-secondary rounded-full overflow-hidden">
        <div 
          className={`h-full ${color} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-xs font-medium w-12 text-right">{value}</span>
    </div>
  );
};

const AnalyticsDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState(null);
  const [demographics, setDemographics] = useState(null);
  const [geography, setGeography] = useState(null);
  const [features, setFeatures] = useState(null);
  const [subscriptions, setSubscriptions] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [retention, setRetention] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [overviewRes, usersRes, demoRes, geoRes, featRes, subsRes, trafficRes, retRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/overview`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics/users`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics/demographics`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics/geography`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics/feature-usage`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics/subscriptions`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics/traffic`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics/retention`, { withCredentials: true })
      ]);

      setOverview(overviewRes.data);
      setUsers(usersRes.data);
      setDemographics(demoRes.data);
      setGeography(geoRes.data);
      setFeatures(featRes.data);
      setSubscriptions(subsRes.data);
      setTraffic(trafficRes.data);
      setRetention(retRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'users', label: 'Users' },
    { id: 'subscriptions', label: 'Revenue' },
    { id: 'traffic', label: 'Traffic' },
    { id: 'features', label: 'Features' },
    { id: 'demographics', label: 'Demographics' },
    { id: 'geography', label: 'Geography' },
    { id: 'retention', label: 'Retention' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="analytics-title">
          Analytics Dashboard
        </h2>
        <Button variant="outline" size="sm" onClick={fetchAnalytics} data-testid="refresh-analytics-btn">
          <RefreshCw className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b pb-3">
        {tabs.map(tab => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab(tab.id)}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && overview && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              title="Total Users" 
              value={overview.total_users} 
              subtitle={`+${overview.new_users_week} this week`}
              icon={Users}
            />
            <StatCard 
              title="Active Subscribers" 
              value={overview.active_subscribers}
              subtitle={`${overview.conversion_rate}% conversion`}
              icon={TrendingUp}
            />
            <StatCard 
              title="Total Revenue" 
              value={`$${overview.total_revenue.toLocaleString()}`}
              subtitle={`$${overview.monthly_revenue} this month`}
              icon={DollarSign}
            />
            <StatCard 
              title="Page Views" 
              value={overview.page_views_week}
              subtitle={`${overview.page_views_today} today`}
              icon={Eye}
            />
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && users && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily Active Users */}
            <Card className="p-5 border border-primary/10">
              <h3 className="text-lg font-medium mb-4">Daily Active Users (Last 30 Days)</h3>
              <div className="h-48 flex items-end gap-1">
                {users.daily_active.slice(-30).map((day, i) => {
                  const maxVal = Math.max(...users.daily_active.map(d => d.active_users), 1);
                  const height = (day.active_users / maxVal) * 100;
                  return (
                    <div 
                      key={i}
                      className="flex-1 bg-primary/80 rounded-t hover:bg-primary transition-colors"
                      style={{ height: `${Math.max(height, 2)}%` }}
                      title={`${day.date}: ${day.active_users} users`}
                    />
                  );
                })}
              </div>
              <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                <span>30 days ago</span>
                <span>Today</span>
              </div>
            </Card>

            {/* Signups Over Time */}
            <Card className="p-5 border border-primary/10">
              <h3 className="text-lg font-medium mb-4">New Signups (Last 30 Days)</h3>
              <div className="h-48 flex items-end gap-1">
                {users.signups_by_day.slice(-30).map((day, i) => {
                  const maxVal = Math.max(...users.signups_by_day.map(d => d.signups), 1);
                  const height = (day.signups / maxVal) * 100;
                  return (
                    <div 
                      key={i}
                      className="flex-1 bg-green-500/80 rounded-t hover:bg-green-500 transition-colors"
                      style={{ height: `${Math.max(height, 2)}%` }}
                      title={`${day.date}: ${day.signups} signups`}
                    />
                  );
                })}
              </div>
              <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                <span>30 days ago</span>
                <span>Today</span>
              </div>
            </Card>
          </div>

          {/* Monthly Active */}
          <Card className="p-5 border border-primary/10">
            <h3 className="text-lg font-medium mb-4">Monthly Active Users</h3>
            <div className="space-y-2">
              {users.monthly_active.map((month, i) => {
                const maxVal = Math.max(...users.monthly_active.map(m => m.active_users), 1);
                return (
                  <ChartBar 
                    key={i}
                    label={month.month}
                    value={month.active_users}
                    maxValue={maxVal}
                  />
                );
              })}
            </div>
          </Card>
        </div>
      )}

      {/* Subscriptions Tab */}
      {activeTab === 'subscriptions' && subscriptions && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard 
              title="Paying Users" 
              value={subscriptions.total_paying_users}
              icon={Users}
            />
            <StatCard 
              title="Avg Revenue/User" 
              value={`$${subscriptions.average_revenue_per_user}`}
              icon={DollarSign}
            />
            <StatCard 
              title="Expiring Soon" 
              value={subscriptions.expiring_soon}
              subtitle="Next 30 days"
              icon={Calendar}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Subscription Types */}
            <Card className="p-5 border border-primary/10">
              <h3 className="text-lg font-medium mb-4">Subscription Types</h3>
              <div className="space-y-3">
                {subscriptions.subscription_type_distribution.map((type, i) => {
                  const total = subscriptions.subscription_type_distribution.reduce((acc, t) => acc + t.count, 0);
                  return (
                    <ChartBar 
                      key={i}
                      label={type.type === 'annual' ? 'Annual ($35.99/yr)' : 'Monthly ($4.99/mo)'}
                      value={type.count}
                      maxValue={total}
                      color={type.type === 'annual' ? 'bg-primary' : 'bg-blue-500'}
                    />
                  );
                })}
              </div>
            </Card>

            {/* Revenue by Month */}
            <Card className="p-5 border border-primary/10">
              <h3 className="text-lg font-medium mb-4">Revenue by Month</h3>
              <div className="h-48 flex items-end gap-2">
                {subscriptions.revenue_by_month.slice(-12).map((month, i) => {
                  const maxVal = Math.max(...subscriptions.revenue_by_month.map(m => m.revenue), 1);
                  const height = (month.revenue / maxVal) * 100;
                  return (
                    <div 
                      key={i}
                      className="flex-1 flex flex-col items-center"
                    >
                      <div 
                        className="w-full bg-green-500/80 rounded-t hover:bg-green-500 transition-colors"
                        style={{ height: `${Math.max(height, 2)}%` }}
                        title={`${month.month}: $${month.revenue}`}
                      />
                      <span className="text-[10px] text-muted-foreground mt-1 rotate-45 origin-left">
                        {month.month.slice(5)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>

          {/* Churn Warning */}
          {subscriptions.churned_users > 0 && (
            <Card className="p-5 border border-yellow-500/30 bg-yellow-50/50 dark:bg-yellow-900/10">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-yellow-500/20">
                  <TrendingUp className="w-5 h-5 text-yellow-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-medium text-yellow-800 dark:text-yellow-200">
                    {subscriptions.churned_users} users with expired subscriptions
                  </p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    Consider sending a re-engagement email to bring them back
                  </p>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Traffic Tab */}
      {activeTab === 'traffic' && traffic && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard 
              title="Unique Visitors Today" 
              value={traffic.unique_visitors_today}
              icon={Eye}
            />
            <StatCard 
              title="Unique Visitors (Week)" 
              value={traffic.unique_visitors_week}
              icon={Users}
            />
            <StatCard 
              title="Total Page Views" 
              value={traffic.daily_page_views.reduce((acc, d) => acc + d.views, 0)}
              subtitle="Last 30 days"
              icon={BarChart3}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Page Views Chart */}
            <Card className="p-5 border border-primary/10">
              <h3 className="text-lg font-medium mb-4">Daily Page Views</h3>
              <div className="h-48 flex items-end gap-1">
                {traffic.daily_page_views.slice(-30).map((day, i) => {
                  const maxVal = Math.max(...traffic.daily_page_views.map(d => d.views), 1);
                  const height = (day.views / maxVal) * 100;
                  return (
                    <div 
                      key={i}
                      className="flex-1 bg-blue-500/80 rounded-t hover:bg-blue-500 transition-colors"
                      style={{ height: `${Math.max(height, 2)}%` }}
                      title={`${day.date}: ${day.views} views`}
                    />
                  );
                })}
              </div>
            </Card>

            {/* Top Pages */}
            <Card className="p-5 border border-primary/10">
              <h3 className="text-lg font-medium mb-4">Top Pages</h3>
              <div className="space-y-2">
                {traffic.page_views_by_page.slice(0, 8).map((page, i) => {
                  const maxVal = traffic.page_views_by_page[0]?.views || 1;
                  return (
                    <ChartBar 
                      key={i}
                      label={page.page || 'Unknown'}
                      value={page.views}
                      maxValue={maxVal}
                      color="bg-blue-500"
                    />
                  );
                })}
              </div>
            </Card>
          </div>

          {/* Top Referrers */}
          {traffic.top_referrers.length > 0 && (
            <Card className="p-5 border border-primary/10">
              <h3 className="text-lg font-medium mb-4">Top Referrers</h3>
              <div className="space-y-2">
                {traffic.top_referrers.map((ref, i) => {
                  const maxVal = traffic.top_referrers[0]?.count || 1;
                  return (
                    <ChartBar 
                      key={i}
                      label={ref.referrer}
                      value={ref.count}
                      maxValue={maxVal}
                      color="bg-purple-500"
                    />
                  );
                })}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Features Tab */}
      {activeTab === 'features' && features && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              title="Journal Entries" 
              value={features.journal_entries.total}
              subtitle={`${features.journal_entries.this_week} this week`}
              icon={BookOpen}
            />
            <StatCard 
              title="Bookmarks" 
              value={features.bookmarks.total}
              subtitle={`${features.bookmarks.this_week} this week`}
              icon={Bookmark}
            />
            <StatCard 
              title="Audio Plays" 
              value={features.audio_plays.total}
              subtitle={`${features.audio_plays.this_week} this week`}
              icon={Volume2}
            />
            <StatCard 
              title="Share Clicks" 
              value={features.share_clicks}
              icon={Share2}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* All Time Feature Usage */}
            <Card className="p-5 border border-primary/10">
              <h3 className="text-lg font-medium mb-4">Feature Usage (All Time)</h3>
              <div className="space-y-2">
                {features.feature_usage_all_time.slice(0, 10).map((feat, i) => {
                  const maxVal = features.feature_usage_all_time[0]?.count || 1;
                  return (
                    <ChartBar 
                      key={i}
                      label={feat.feature || 'Unknown'}
                      value={feat.count}
                      maxValue={maxVal}
                    />
                  );
                })}
                {features.feature_usage_all_time.length === 0 && (
                  <p className="text-sm text-muted-foreground">No feature usage data yet</p>
                )}
              </div>
            </Card>

            {/* This Week Feature Usage */}
            <Card className="p-5 border border-primary/10">
              <h3 className="text-lg font-medium mb-4">Feature Usage (This Week)</h3>
              <div className="space-y-2">
                {features.feature_usage_this_week.slice(0, 10).map((feat, i) => {
                  const maxVal = features.feature_usage_this_week[0]?.count || 1;
                  return (
                    <ChartBar 
                      key={i}
                      label={feat.feature || 'Unknown'}
                      value={feat.count}
                      maxValue={maxVal}
                      color="bg-green-500"
                    />
                  );
                })}
                {features.feature_usage_this_week.length === 0 && (
                  <p className="text-sm text-muted-foreground">No feature usage this week</p>
                )}
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Demographics Tab */}
      {activeTab === 'demographics' && demographics && (
        <div className="space-y-6">
          <Card className="p-5 border border-primary/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Demographics Completion</h3>
              <span className="text-2xl font-semibold">{demographics.demographics_completion_rate}%</span>
            </div>
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary transition-all duration-500"
                style={{ width: `${demographics.demographics_completion_rate}%` }}
              />
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Percentage of users who provided demographic information
            </p>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Age Distribution */}
            <Card className="p-5 border border-primary/10">
              <h3 className="text-lg font-medium mb-4">Age Distribution</h3>
              <div className="space-y-2">
                {demographics.age_distribution.map((age, i) => {
                  const maxVal = Math.max(...demographics.age_distribution.map(a => a.value), 1);
                  return (
                    <ChartBar 
                      key={i}
                      label={age.label}
                      value={age.value}
                      maxValue={maxVal}
                    />
                  );
                })}
                {demographics.age_distribution.length === 0 && (
                  <p className="text-sm text-muted-foreground">No age data available</p>
                )}
              </div>
            </Card>

            {/* Gender Distribution */}
            <Card className="p-5 border border-primary/10">
              <h3 className="text-lg font-medium mb-4">Gender Distribution</h3>
              <div className="space-y-2">
                {demographics.gender_distribution.map((gender, i) => {
                  const maxVal = Math.max(...demographics.gender_distribution.map(g => g.value), 1);
                  return (
                    <ChartBar 
                      key={i}
                      label={gender.label}
                      value={gender.value}
                      maxValue={maxVal}
                      color="bg-purple-500"
                    />
                  );
                })}
                {demographics.gender_distribution.length === 0 && (
                  <p className="text-sm text-muted-foreground">No gender data available</p>
                )}
              </div>
            </Card>

            {/* Faith Background */}
            <Card className="p-5 border border-primary/10">
              <h3 className="text-lg font-medium mb-4">Faith Background</h3>
              <div className="space-y-2">
                {demographics.faith_distribution.map((faith, i) => {
                  const maxVal = Math.max(...demographics.faith_distribution.map(f => f.value), 1);
                  return (
                    <ChartBar 
                      key={i}
                      label={faith.label}
                      value={faith.value}
                      maxValue={maxVal}
                      color="bg-amber-500"
                    />
                  );
                })}
                {demographics.faith_distribution.length === 0 && (
                  <p className="text-sm text-muted-foreground">No faith background data available</p>
                )}
              </div>
            </Card>

            {/* How Found Us */}
            <Card className="p-5 border border-primary/10">
              <h3 className="text-lg font-medium mb-4">How Users Found Us</h3>
              <div className="space-y-2">
                {demographics.source_distribution.map((source, i) => {
                  const maxVal = Math.max(...demographics.source_distribution.map(s => s.value), 1);
                  return (
                    <ChartBar 
                      key={i}
                      label={source.label}
                      value={source.value}
                      maxValue={maxVal}
                      color="bg-cyan-500"
                    />
                  );
                })}
                {demographics.source_distribution.length === 0 && (
                  <p className="text-sm text-muted-foreground">No source data available</p>
                )}
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Geography Tab */}
      {activeTab === 'geography' && geography && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Country Distribution */}
            <Card className="p-5 border border-primary/10">
              <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5" strokeWidth={1.5} />
                Top Countries
              </h3>
              <div className="space-y-2">
                {geography.country_distribution.map((country, i) => {
                  const maxVal = geography.country_distribution[0]?.value || 1;
                  return (
                    <ChartBar 
                      key={i}
                      label={country.label}
                      value={country.value}
                      maxValue={maxVal}
                    />
                  );
                })}
                {geography.country_distribution.length === 0 && (
                  <p className="text-sm text-muted-foreground">No country data available</p>
                )}
              </div>
            </Card>

            {/* Region Distribution */}
            <Card className="p-5 border border-primary/10">
              <h3 className="text-lg font-medium mb-4">Top Regions</h3>
              <div className="space-y-2">
                {geography.region_distribution.slice(0, 10).map((region, i) => {
                  const maxVal = geography.region_distribution[0]?.value || 1;
                  return (
                    <ChartBar 
                      key={i}
                      label={region.label}
                      value={region.value}
                      maxValue={maxVal}
                      color="bg-blue-500"
                    />
                  );
                })}
                {geography.region_distribution.length === 0 && (
                  <p className="text-sm text-muted-foreground">No region data available</p>
                )}
              </div>
            </Card>

            {/* City Distribution */}
            <Card className="p-5 border border-primary/10">
              <h3 className="text-lg font-medium mb-4">Top Cities</h3>
              <div className="space-y-2">
                {geography.city_distribution.slice(0, 10).map((city, i) => {
                  const maxVal = geography.city_distribution[0]?.value || 1;
                  return (
                    <ChartBar 
                      key={i}
                      label={city.label}
                      value={city.value}
                      maxValue={maxVal}
                      color="bg-green-500"
                    />
                  );
                })}
                {geography.city_distribution.length === 0 && (
                  <p className="text-sm text-muted-foreground">No city data available</p>
                )}
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Retention Tab */}
      {activeTab === 'retention' && retention && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              title="Read Today" 
              value={retention.users_read_today}
              icon={BookOpen}
            />
            <StatCard 
              title="Read Yesterday" 
              value={retention.users_read_yesterday}
              icon={Calendar}
            />
            <StatCard 
              title="Avg Current Streak" 
              value={`${retention.average_current_streak} days`}
              icon={Flame}
            />
            <StatCard 
              title="Week Retention" 
              value={`${retention.week_retention_rate}%`}
              icon={TrendingUp}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Streak Distribution */}
            <Card className="p-5 border border-primary/10">
              <h3 className="text-lg font-medium mb-4">Users by Streak Length</h3>
              <div className="space-y-2">
                {retention.streak_distribution.map((streak, i) => {
                  const maxVal = Math.max(...retention.streak_distribution.map(s => s.count), 1);
                  return (
                    <ChartBar 
                      key={i}
                      label={streak.range}
                      value={streak.count}
                      maxValue={maxVal}
                      color={streak.range === '0 days' ? 'bg-red-400' : 'bg-primary'}
                    />
                  );
                })}
              </div>
            </Card>

            {/* Streak Stats */}
            <Card className="p-5 border border-primary/10">
              <h3 className="text-lg font-medium mb-4">Streak Statistics</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                  <span className="text-sm">Average Current Streak</span>
                  <span className="text-xl font-semibold">{retention.average_current_streak} days</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                  <span className="text-sm">Average Longest Streak</span>
                  <span className="text-xl font-semibold">{retention.average_longest_streak} days</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                  <span className="text-sm">Week-over-Week Retention</span>
                  <span className="text-xl font-semibold">{retention.week_retention_rate}%</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
